import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomFileUpload from "../../elements/CustomFileUpload";
import {
  setGlobalLoader,
  showSnackbar,
} from "../../redux/actions/commonAction";
import { getAllCredentials } from "../../redux/actions/credentialAction";
import {
  getImportServices,
  getRegionsByAccount,
  importStateFile,
} from "../../redux/actions/stateFileAction";
import Types from "../../redux/types";
import { debounce, validateStatefilePath } from "../../utils/helpers";
import CodeReport from "./reports/codeReport";
import { useStyles } from "./styles";

const ImportFile = () => {
  const dispatch = useDispatch();
  const { allCredentials } = useSelector((state) => state?.credential);
  const { allRegions } = useSelector((state) => state?.stateFile);
  const classes = useStyles();

  const [fileType, setFileType] = useState("url");
  const [report, setReport] = useState(false);
  const [urlError, setUrlError] = useState("");
  const [state, setState] = useState({
    credential: null,
    service: [],
    region: "",
    fileExists: false,
    url: "",
    file: null,
  });
  const [serviceOptions, setServices] = useState([]);
  const [dropDownLoading, setDropDownLoading] = useState(false);

  useEffect(() => {
    dispatch(setGlobalLoader(true));
    dispatch(getAllCredentials());
    dispatch(getImportServices());
    dispatch({
      type: Types.REGIONS_BY_ACCOUNT,
      payload: [],
    });
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (allCredentials?.length > 0) {
      dispatch(setGlobalLoader(false));
    }
    //eslint-disable-next-line
  }, [allCredentials]);

  const updateLocalState = (key, value = "") => {
    let newKeys = {};
    if (key === "credential") {
      newKeys["region"] = "";
      newKeys["service"] = [];
      dispatch({
        type: Types.REGIONS_BY_ACCOUNT,
        payload: [],
      });
    }
    if (key === "region") {
      newKeys["service"] = [];
    }
    setState((prevState) => ({
      ...prevState,
      ...newKeys,
      [key]: value,
    }));
  };

  const handleChange = (key, value) => {
    updateLocalState(key, value);
    if (value) {
      // Checks whether credential is changed and api call is fired for the regions
      if (key === "credential") {
        setDropDownLoading(true);
        dispatch(
          showSnackbar({
            message:
              "Retrieving the regions available for your account, please wait...",
            severity: "info",
            open: true,
          })
        );
        dispatch(
          getRegionsByAccount(value?.credName, () => setDropDownLoading(false))
        );
      }

      if (key === "region") {
        setServices(value?.unique_services || []);
      }

      // Replace the select all keyword for the api
      if (key === "service" && value.find((ele) => ele === "Select All")) {
        setState((prevState) => ({
          ...prevState,
          [key]: ["Select All"],
        }));
      }
    }
  };

  const debouncedValidation = debounce((val) => {
    if (val?.length > 0 && !validateStatefilePath(val)) {
      setUrlError("Invalid S3 Path");
    } else {
      setUrlError("");
    }
  }, 500);

  const formatSelectedServices = (serviceState) => {
    if (serviceState.length === 1) {
      if (serviceState[0] === "Select All") {
        return "all";
      } else {
        return serviceState[0];
      }
    } else {
      return serviceState.join(",");
    }
  };

  const handleSubmit = () => {
    const selectedServices = formatSelectedServices(state?.service);
    const payload = {
      services: selectedServices,
      cred_name: state?.credential?.credName,
      region: state?.region?.region,
      s3_url: state?.url,
      file: state.file,
      file_exists: fileType === "file",
    };
    dispatch(
      importStateFile(payload, () => {
        setReport(true);
      })
    );
  };

  const onReportClose = () => {
    setState({
      credential: null,
      service: [],
      region: "",
      fileExists: true,
      url: "",
    });
    setReport(false);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom={true}>
        Migrate to As Code
      </Typography>
      {!report ? (
        <>
          <Box className={classes.selectView}>
            <Autocomplete
              size="small"
              options={allCredentials}
              value={state.credential}
              onChange={(e, value) => {
                handleChange("credential", value);
              }}
              sx={{ width: 300 }}
              getOptionLabel={(option) => option.accountNumber}
              renderInput={(params) => (
                <TextField {...params} label="Accounts" />
              )}
            />
            <Autocomplete
              size="small"
              disabled={!(allRegions?.length > 0)}
              options={allRegions}
              value={state.region}
              onChange={(e, value) => {
                handleChange("region", value);
              }}
              sx={{ width: 300 }}
              getOptionLabel={(option) => option?.region || ""}
              renderInput={(params) => <TextField {...params} label="Region" />}
            />
            <Autocomplete
              size="small"
              disabled={!state.region?.region}
              multiple
              /* Add when select allfunctionality is available in the api **/
              // options={state.service[0] == 'Select All' ? [] : ["Select All", ...allServices]}
              options={serviceOptions}
              sx={{ minWidth: 300 }}
              value={state.service}
              onChange={(e, value) => {
                handleChange("service", value);
              }}
              getOptionLabel={(option) => option}
              renderInput={(params) => (
                <TextField {...params} label="Service" />
              )}
            />
            {dropDownLoading && (
              <CircularProgress className={classes.loader} size={30} />
            )}
          </Box>
          <Typography variant="subtitle2" gutterBottom={true}>
            Do you have existing state file
          </Typography>
          <RadioGroup
            row
            value={state.fileExists?.toString()}
            onChange={(e) =>
              handleChange("fileExists", e.target.value === "true")
            }
          >
            <FormControlLabel
              labelPlacement="start"
              value="true"
              control={<Radio />}
              label="Yes"
            />
            <FormControlLabel
              labelPlacement="start"
              value="false"
              control={<Radio />}
              label="No"
            />
          </RadioGroup>
          {state.fileExists && (
            <>
              <Typography variant="subtitle2" gutterBottom={true}>
                State file
              </Typography>
              <RadioGroup
                row
                value={fileType}
                onChange={(e) => setFileType(e.target.value)}
              >
                <FormControlLabel
                  labelPlacement="start"
                  value="url"
                  control={<Radio />}
                  label="S3 bucket path"
                />
                <FormControlLabel
                  labelPlacement="start"
                  value="file"
                  control={<Radio />}
                  label="Upload state file"
                />
              </RadioGroup>
              {fileType === "file" ? (
                <Box my={2}>
                  <CustomFileUpload
                    title="Upload state file"
                    value={state.file}
                    onChange={(file) => handleChange("file", file)}
                  />
                </Box>
              ) : (
                <TextField
                  size="small"
                  fullWidth
                  required
                  value={state.url}
                  onChange={(e) => {
                    handleChange("url", e.target.value);
                    debouncedValidation(e.target.value);
                  }}
                  label="S3 Bucket Path"
                  multiline
                  margin="normal"
                  error={Boolean(urlError)}
                  helperText={urlError}
                  maxRows={2}
                />
              )}
            </>
          )}

          <Box className={classes.footerView}>
            <Button
              disabled={
                !(
                  state.credential &&
                  state.region &&
                  state.service.length > 0 &&
                  (!state.fileExists ||
                    (fileType === "file"
                      ? state.file
                      : state.url?.length > 0 && !urlError))
                )
              }
              onClick={handleSubmit}
              variant="contained"
            >
              Submit
            </Button>
          </Box>
        </>
      ) : (
        <CodeReport state={state} onClose={onReportClose} />
      )}
    </Box>
  );
};

export default ImportFile;
